import { Logger, LogLevel } from '@openticket/lib-shop';
import { AxiosInstance } from 'axios';

const EVENT_ID_WHITELIST: string[] = [
    'vangnet',
    'safetynetwl',
    'petersqueueittestall',
    'openticketdev',
] as const;

interface QueueItWaitingRoomConfig {
    queueItClientLoaded: boolean;
    queueItCustomerId: string;
    queueItEventId: string;
    queueItHost: string;
}

export function registerQueueIt(
    log: Logger,
    shopApiUrl: string,
    http?: AxiosInstance | undefined
): boolean {
    if (!http) {
        return false;
    }

    http.interceptors.response.use(response => {
        if (
            response.status === 403 &&
            new URL(response.config.url || '').hostname ===
                new URL(shopApiUrl).hostname
        ) {
            const queueItConfig = getQueueItConfigValues();
            // Log about the block
            log.create(
                LogLevel.Info,
                ['queueit', 'request', 'blocked'],
                {
                    queueit_client_loaded: queueItConfig.queueItClientLoaded,
                    queueit_customer_id: queueItConfig.queueItCustomerId,
                    queueit_event_id: queueItConfig.queueItEventId,
                    queueit_host: queueItConfig.queueItHost,
                },
                'Gatekeeper blocked request.'
            );
            throw Error('Gatekeeper blocked request.');
        }
        return response;
    });

    http.interceptors.request.use(request => {
        const queueItHeader = getQueueItHeader(window.initialLocationHref, log);

        const apiHost = new URL(shopApiUrl).hostname;
        const interceptedHost = new URL(request.url || '').hostname;
        // Only inject for known host
        if (queueItHeader && apiHost && interceptedHost === apiHost) {
            request.headers.set(queueItHeader[0], queueItHeader[1]);
        }
        return request;
    });

    return true;
}

function queueItConfigReady(log: Logger): boolean {
    if (
        window.QueueIt &&
        typeof window.QueueIt === 'object' &&
        typeof window.QueueIt.isIntegrationConfigReady === 'function' &&
        window.QueueIt.isIntegrationConfigReady() &&
        window.myQueueClient &&
        typeof window.myQueueClient === 'object' &&
        window.myQueueClient.customerId &&
        window.myQueueClient.eventId &&
        window.myQueueClient.options &&
        typeof window.myQueueClient.options === 'object' &&
        window.myQueueClient.options.host
    ) {
        return true;
    }

    log.create(
        LogLevel.Error,
        ['queueit', 'init', 'queueitclient', 'failed'],
        {
            queueit_client_loaded: !!window.myQueueClient,
            queueit_customer_id:
                (window.myQueueClient &&
                    typeof window.myQueueClient === 'object' &&
                    window.myQueueClient.customerId) ||
                '',
            queueit_event_id:
                (window.myQueueClient &&
                    typeof window.myQueueClient === 'object' &&
                    window.myQueueClient.eventId) ||
                '',
            queueit_host:
                (window.myQueueClient &&
                    typeof window.myQueueClient === 'object' &&
                    window.myQueueClient.options &&
                    typeof window.myQueueClient.options === 'object' &&
                    window.myQueueClient.options.host) ||
                '',
        },
        'QueueIt client initialisation failed'
    );

    return false;
}

function getQueueItHeader(
    initialLocation: string,
    log: Logger
): [string, string] | null {
    const initialLocationUrl = new URL(initialLocation);
    initialLocationUrl.searchParams.set('sessionId', log.id);

    const queryParamQueueItToken = (
        initialLocationUrl.searchParams.get('queueittoken') || ''
    ).trim();

    if (!queueItConfigReady(log)) {
        return null;
    }

    const queueItConfig = getQueueItConfigValues();
    if (queryParamQueueItToken) {
        // DD-4E51 This is the wanted flow.
        // This token is only legitimately present when the visitor arrived here from a QueueIt waitingroom.
        // (This waiting room need not take up any time, but it should be hit.)
        logValidQueueItTokenFound(log, queryParamQueueItToken, queueItConfig);

        return ['x-queueit-ajaxpageurl', queryParamQueueItToken];
    }

    // No token found in url, try to get from cookie
    const queueItCookieToken = getQueueItCookie();
    if (queueItCookieToken) {
        logValidQueueItCookieFound(log, queueItCookieToken, queueItConfig);

        return ['x-queueit-cookies', queueItCookieToken];
    }

    // No token found in url or cookies, check if it is a whitelisted eventid
    const waitingRoomWhitelisted = EVENT_ID_WHITELIST.includes(
        queueItConfig.queueItEventId
    );

    // DD-4E53 QueueIt Event ID is Whitelisted. Continue without a queueit token.
    // (Also continue when we have no token, hoping that it is not required for this shop)
    log.create(
        LogLevel.Info,
        ['queueit', 'token', 'missing', 'error'],
        {
            queueit_client_loaded: queueItConfig.queueItClientLoaded,
            queueit_customer_id: queueItConfig.queueItCustomerId,
            queueit_event_id: queueItConfig.queueItEventId,
            queueit_host: queueItConfig.queueItHost,
            queueit_event_whitelisted: waitingRoomWhitelisted,
        },
        'QueueIt token missing for waiting room.'
    );

    return null;
}

function logValidQueueItTokenFound(
    log: Logger,
    queueItToken: string,
    queueItConfig: QueueItWaitingRoomConfig
): void {
    const queueItTokenValues = Object.fromEntries(
        // e_{waiting room id}
        // q_{uuid...}
        // ts_{seconds since 1970-01-01 00:00:00 UTC...}
        // ce_true
        // rt_queue
        // h_{09af...}
        queueItToken.split('~').map((kv: string) => {
            const [k, ...v] = kv.split('_');
            return [k, v.join('_')];
        })
    );

    log.create(
        LogLevel.Info,
        ['queueit', 'token', 'found'],
        {
            queueit_client_loaded: queueItConfig.queueItClientLoaded,
            queueit_customer_id: queueItConfig.queueItCustomerId,
            queueit_event_id: queueItConfig.queueItEventId,
            queueit_host: queueItConfig.queueItHost,

            queueit_token_event_id: queueItTokenValues.e || '',
            queueit_token_ts: queueItTokenValues.ts || '',
            queueit_token_redirect_type: queueItTokenValues.rt || '',
        },
        'QueueIt token found'
    );
}

function logValidQueueItCookieFound(
    log: Logger,
    queueItCookie: string,
    queueItConfig: QueueItWaitingRoomConfig
): void {
    log.create(
        LogLevel.Info,
        ['queueit', 'cookie', 'found'],
        {
            queueit_client_loaded: queueItConfig.queueItClientLoaded,
            queueit_customer_id: queueItConfig.queueItCustomerId,
            queueit_event_id: queueItConfig.queueItEventId,
            queueit_host: queueItConfig.queueItHost,
            queueit_cookie: queueItCookie || '',
        },
        'QueueIt cookie found'
    );
}

function getQueueItConfigValues(): QueueItWaitingRoomConfig {
    return {
        queueItClientLoaded: !!window.myQueueClient,
        queueItCustomerId: window.myQueueClient.customerId,
        queueItEventId: window.myQueueClient.eventId,
        queueItHost: window.myQueueClient.options.host,
    };
}

export function getQueueItCookie(): string | null {
    try {
        // Use QueueItClient to get the most appropriate cookie
        if (
            'myQueueClient' in window &&
            window.myQueueClient &&
            typeof window.myQueueClient === 'object' &&
            'cookieManager' in window.myQueueClient &&
            window.myQueueClient.cookieManager &&
            typeof window.myQueueClient.cookieManager === 'object' &&
            'getQueueStateCookie' in window.myQueueClient.cookieManager &&
            typeof window.myQueueClient.cookieManager.getQueueStateCookie ===
                'function'
        ) {
            const cookie = window.myQueueClient.cookieManager.getQueueStateCookie();
            if (cookie !== undefined && cookie !== null && cookie !== '') {
                return cookie;
            }
        }
    } catch {
        /* No-op */
    }
    return null;
}
